import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { getDetailProduct } from '../services/products'

export const getProductDetail = createAsyncThunk('product/getProductDetail', async (productId) => {
  const params = {
    'include': 'country,images,options,variations,wholesale_price,bulk_prices,supplier,warehouses,supplier,owner,brand,exclusive,creator,source,updater,videos,recommendations',
    // timestamp: `${new Date().getTime()}`, temporary remove to avoid cache issue
  }
  const response = await getDetailProduct(productId, params)
  return response
})

const initialState = {
  isLoadingProduct: true,
  attributes: {},
  included: {},
  relationships: {},
  summary: {
    productId: 0,
    aliSku: '',
    sku: '',
    title: '',
    slug: '',
    description: '',
    priority: 0,

    status: {
      id: 0,
      name: '',
    },

    image: null,

    originalCurrency: '',
    regularPrice: 0,
    salePrice: 0,
    resellerPrice: 0,
    markupPercentage: 0,

    visibility: false,
    notShowInCatalog: false,
    exportOnly: false,

    rewardPoint: 0,
    realQtyReview: 0,
    realAverageReviewPoint: 0,
    baseQtyReview: 0,
    baseAverageReviewPoint: 0,

    quantityUnit: '',
    quantity: 0,
    quantitySold: 0,
    minimumQuantity: 0,
    groupQty: 0,
    quantityPerCbm: 0,
    realQtySold: 0,
    baseQtySold: 0,

    dimensionUnit: '',
    dimensions: [
      {
        label: 'width',
        value: '1',
      },
      {
        label: 'length',
        value: '1',
      },
      {
        label: 'height',
        value: '1',
      },
    ],

    weightUnit: '',
    weight: 0,

    originalProductId: 0,

    note: '',
    merchant: '',

    source: {
      id: 0,
      name: '',
    },
    supplier: {
      id: 0,
      name: '',
    },
    brand: {
      id: 0,
      name: '',
    },
    warehouse: {
      id: 0,
      name: '',
    },

    createdAt: '',
    updatedAt: '',
    creator: {
      id: 0,
      name: '',
    },
    updater: {
      id: 0,
      name: '',
    },

    countryId: 0,
    provinceId: 0,
    cityId: 0,
    districtId: 0,

    tags: [],
    poShipFrom: null,
    poShipmentMethod: '',

    categories: [],
  },

  variants: {
    origin: [],
    structure: [],
    attributes: [],
    sku: [],
  },

  origin: {
    originalLanguageId: 0,
    originalLanguage: '',
    originalTitle: '',
    originalDescription: '',
    originalUrl: '',
    originalSourceUrl: '',
    originalCountryId: 0,
    originalCountry: '',
    originalProductId: 0,
  },

  prices: {
    enablePricing: false,
    pricingType: '',
    isExclusive: false,
    wholesalePrices: [],
    bundlePrices: [],
  },

  minimumPayload: {
    title: '',
    slug: '',
    currency: '',
    quantity: 0,
    unit: '',
    weight: 0,
    weight_unit: '',
    minimum_qty: 0,
    regular_price: 0,
    sale_price: 0,
    promo_price: 0,
    description: '',
    categories: [],
    country_id: null,
    province_id: null,
    city_id: null,
    dimensions: {},
    dimension_unit: '',
    sku: '',
    ali_sku: '',
    wholesale_prices: {},
    supplier_id: null,
  }
}

const productDetail = createSlice({
  name: 'product_detail',
  initialState,
  reducers: {
    productDetailState: (state, action) => {
      state.isLoadingProduct = action.payload.isLoadingProduct
      state.attributes = action.payload.attributes
      state.included = action.payload.included
      state.relationships = action.payload.relationships
      state.summary = action.payload.summary
      state.origin = action.payload.origin
      state.variants = action.payload.origin
    },
    updateProductSummary: (state, action) => {
      state.summary = action.payload
    },
    updateVariants: (state, action) => {
      state.variants = action.payload
    },
    updateProductOrigin: (state, action) => {
      state.origin = action.payload
    },
    updateProductPrices: (state, action) => {
      state.prices = action.payload
    },
    updateMinimumPayload: (state, action) => {
      state.minimumPayload = action.payload
    },

    // clear Product Detail
    resetProduct: () => initialState, // 🔥 important
  },
  extraReducers: {
    [getProductDetail.pending]: (state) => {
      state.isLoadingProduct = true
    },
    [getProductDetail.fulfilled]: (state, action) => {
      state.attributes = action.payload.data.attributes
      state.included = action.payload.included
      state.relationships = action.payload.data.relationships
      state.summary = setSummary(action.payload)
      state.variants = setVariants(action.payload)
      state.origin = setOrigin(action.payload)
      state.prices = setPrices(action.payload)
      state.minimumPayload = setMinimumPayload(action.payload)
      state.isLoadingProduct = false
    },
    [getProductDetail.rejected]: (state) => {
      state.isLoadingProduct = false
    }
  }
})

const handleDataIncluded = (data, key, id) => {
  return data[key][id]?.attributes || {}
}

const setSummary = (payload) => {
  const attributes = payload.data.attributes
  const relationships = payload.data.relationships
  const included = payload.included

  const { tags, categories } = included

  const dataSource = handleDataIncluded(included, 'source', relationships.source.id)
  const dataCreator = handleDataIncluded(included, 'creator', relationships?.creator?.id)
  const dataUpdater = handleDataIncluded(included, 'updater', relationships?.updater?.id)
  const dataStatus = handleDataIncluded(included, 'statuses', relationships?.statuses[0]?.id)
  const dataSupplier = handleDataIncluded(included, 'supplier', relationships?.supplier?.id)
  const dataBrand = handleDataIncluded(included, 'brand', relationships?.brand?.id)
  const dataWarehouse = handleDataIncluded(included, 'warehouses', relationships?.warehouses[0]?.id)

  const dataTags = Object.keys(tags)
  const dataCategories = Object.keys(categories)

  const mappingSummary = {
    productId: attributes.id,
    aliSku: attributes.ali_sku,
    sku: attributes.sku,
    title: attributes.title,
    slug: attributes.slug,
    description: attributes.description,
    priority: attributes.priority,
    createdAt: attributes.created_at,
    updatedAt: attributes.updated_at,

    image: attributes.image,

    originalCurrency: attributes.original_currency,
    regularPrice: +attributes.regular_price,
    salePrice: +attributes.sale_price,
    resellerPrice: +attributes.reseller_price,
    markupPercentage: +attributes.markup_percentage,

    visibility: attributes.visibility,
    notShowInCatalog: attributes.not_show_in_catalog,
    exportOnly: attributes.for_export_only,

    rewardPoint: attributes.reward_point,
    realQtyReview: attributes.real_qty_review,
    realAverageReviewPoint: attributes.real_average_review_point,
    baseQtyReview: attributes.base_qty_review,
    baseAverageReviewPoint: attributes.base_average_review_point,
    baseQtySold: attributes.base_qty_sold,

    quantityUnit: attributes.quantity_unit,
    quantity: attributes.quantity,
    quantitySold: attributes.quantity_sold,
    minimumQuantity: attributes.minimum_qty,
    groupQty: attributes.group_qty,
    quantityPerCbm: attributes.qty_per_cbm,
    realQtySold: attributes.real_qty_sold,

    dimensionUnit: attributes.dimension_unit,
    dimensions: attributes.dimensions,

    weightUnit: attributes.weight_unit,
    weight: attributes.weight,

    originalProductId: attributes.original_product_id,

    note: attributes.note,
    merchant: attributes.merchant,

    source: {
      id: dataSource?.id || 0,
      name: dataSource?.name || '',
    },
    supplier: {
      id: dataSupplier?.id || 0,
      name: dataSupplier?.name || '',
    },
    brand: {
      id: dataBrand?.id || 0,
      name: dataBrand?.name || '',
    },
    warehouse: {
      id: dataWarehouse?.id || 0,
      name: dataWarehouse?.name || ''
    },

    creator: {
      id: dataCreator?.id || 0,
      name: dataCreator?.name || '',
    },
    updater: {
      id: dataUpdater?.id || 0,
      name: dataUpdater?.name || '',
    },
    status: {
      id: dataStatus?.id || 0,
      name: dataStatus?.name || '',
    },
    countryId: attributes.country_id,
    provinceId: attributes.province_id,
    cityId: attributes.city_id,
    districtId: attributes.district_id,

    tags: dataTags,
    poShipFrom: attributes.po_ship_from?.attributes?.id || null,
    poShipmentMethod: attributes.po_shipment_method,

    categories: dataCategories,
  }
  return mappingSummary
}

const setVariants = (payload) => {
  const included = payload.included

  const options = Object.values(included.options)
  const variations = Object.values(included.variations)

  const generatedVariantStructure = options.map((value) => {
    return {
      option: value.attributes.name,
      value: value.attributes.option_value.flatMap((optionValue) => {
        return [optionValue.attributes.value]
      }),
    }
  })

  const generatedVariantOrigin = options.map((value) => {
    return {
      name: value.attributes.name,
      original_name: value.attributes.original_name,
      key_id: value.attributes.id,
      attributes:  value.attributes.option_value.flatMap((optionValue) => {
        return [optionValue.attributes]
      }),
    }
  })

  const generatedAttributes = generatedVariantStructure.flatMap((variant) => {
    return variant.value.map((data) => {
      return {
        key_id: '',
        key: variant.option,
        value: data,
        value_id: null,
        property: '',
        image_url: '',
        original_key: '',
        original_value: data,
      }
    })
  })

  const generatedSku = variations.map((value) => {
    return {
      _combinationIdentification: value.attributes.options_values.flatMap((value) => {
          return `${value.name}:${value.option}`
        }).join(';'),
      items: value.attributes.options_values.map((item) => {
        return {
          key_id: '',
          key: item.name,
          value: item.option,
          value_id: null,
          property: '',
          image_url: '',
          original_key: '',
          original_value: item.original_option,
        }
      }),
      variant_id: value.attributes.id,
      sku_id: value.attributes.sku,
      ali_sku: value.attributes.ali_sku,
      price: value.attributes.regular_price,
      sale_price: value.attributes.sale_price,
      reseller_price: value.attributes.reseller_price,
      price_strike: '',
      quantity: value.attributes.quantity,
      properties: '',
      image_url: value.attributes.image_url,
    }
  })

  const mappingVaroants = {
    origin: generatedVariantOrigin,
    structure: generatedVariantStructure,
    attributes: generatedAttributes,
    sku: generatedSku,
  }

  return mappingVaroants
}

const setOrigin = (payload) => {
  const attributes = payload.data.attributes

  const mappingOrigin = {
    originalLanguageId: attributes.original_language_id,
    originalLanguage: attributes.original_language,
    originalTitle: attributes.original_title,
    originalDescription: attributes.original_description,
    originalUrl: attributes.original_url,
    originalSourceUrl: attributes.original_source_url,
    originalCountry: attributes.original_country,
    originalCountryId: attributes.original_country_id,
    originalProductId: attributes.original_product_id,
  }
  return mappingOrigin
}

const setPrices = (payload) => {
  const attributes = payload.data.attributes
  const included = payload.included
  const { bulk_prices, wholesale_prices } = included

  const wholesalePriceList = Object.values(wholesale_prices)
  const wholesalePrices = wholesalePriceList.map(function (value) {
    return {
      min_quantity: value.attributes.min_quantity,
      price: value.attributes.price,
    }
  })

  const bundlePricesList = Object.values(bulk_prices)
  const bundlePrices = bundlePricesList.map(function (value) {
    const __tempData = {
      type: value.type,
      action: 'update',
      created_at: value.attributes.created_at,
      dimension_unit: value.attributes.dimension_unit,
      dimensions: JSON.parse(JSON.stringify(value.attributes.dimensions)),
      id: value.attributes.id,
      price: value.attributes.price,
      product_id: value.attributes.product_id,
      quantity: value.attributes.quantity,
      unit_name: value.attributes.unit_name,
      updated_at: value.attributes.updated_at,
      weight: value.attributes.weight,
      weight_unit: value.attributes.weight_unit,
    }
    if (!__tempData.dimensions) {
      __tempData.dimensions = [
        { label: 'length', value: null },
        { label: 'width', value: null },
        { label: 'height', value: null },
      ]
    }
    return __tempData
  })

  return {
    isExclusive: attributes.is_exclusive,
    enablePricing: attributes.enable_pricing,
    pricingType: attributes.pricing_type,
    wholesalePrices,
    bundlePrices,
  }
}

const setMinimumPayload = (payload) => {
  const attributes = payload.data.attributes
  const relationships = payload.data.relationships

  const { tags, owner, wholesale_prices } = payload.included

  const generateTagLabel = () => {
    return Object.keys(tags).map(e => +e)
  }
  const generatedPayloadForExistingUser = () => {
    return Object.keys(owner).map(e => +e)
  }

  const categories = relationships.categories
  const pickCategoryId = categories.flatMap((value) => {
    return [value.id]
  })

  const wholesalePrices = relationships.wholesale_prices
  const returnDataWholesale = wholesalePrices.map( (value) => {
    return {
      attributes: {
        min_quantity: wholesale_prices[value.id].attributes.min_quantity,
        max_quantity: wholesale_prices[value.id].attributes.max_quantity,
        price: wholesale_prices[value.id].attributes.price,
      },
    }
  })

  const mappingMinimumPayload = {
    title: attributes.title,
    currency: attributes.original_currency,
    quantity: attributes.quantity,
    unit: attributes.quantity_unit,
    weight: attributes.weight,
    weight_unit: attributes.weight_unit,
    minimum_qty: attributes.minimum_qty,
    regular_price: attributes.regular_price,
    sale_price: attributes.sale_price,
    promo_price: attributes.promo_price,
    description: attributes.description,
    categories: pickCategoryId,
    country_id: attributes.country_id,
    province_id: attributes.province_id,
    city_id: attributes.city_id,
    dimensions: JSON.parse(JSON.stringify(attributes.dimensions)),
    dimension_unit: attributes.dimension_unit,
    sku: attributes.sku,
    ali_sku: attributes.ali_sku,
    wholesale_prices: returnDataWholesale,
    supplier_id: attributes.supplier_id,
    tags: generateTagLabel(),
    user_ids: generatedPayloadForExistingUser(),
  }
  return mappingMinimumPayload
}


export const { productDetailState, updateProductSummary, updateVariants, updateProductOrigin, updateMinimumPayload, updateProductPrices, resetProduct } = productDetail.actions
export default productDetail.reducer